import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Badge } from 'reactstrap';
import { updateUi, getLastHealthCheck, checkHealth } from '../../redux';
import { Health } from '../../services';

import './HealthChecks.css';

const HealthChecks = () => {
    const dispatch             = useDispatch();
    const { t, i18n }          = useTranslation();
    const { showHealthChecks } = useSelector(state => state.ui);
    const { log, isLoading }   = useSelector(state => state.health);

    useEffect(() => {
        dispatch(getLastHealthCheck());
    }, []);

    const check = () => {
        dispatch(checkHealth());
    };

    const hideHealthChecks = () => {
        dispatch(updateUi({ showHealthChecks: !showHealthChecks }));
    };

    const nbPassed = useMemo(() => {
        return Object.keys(log?.results || {}).reduce((nb, group) => {
            return (nb += log.results[group].filter(t => !t.ignored && !!t.result).length);
        }, 0);
    }, [log]);
    const nbIgnored = useMemo(() => {
        return Object.keys(log?.results || {}).reduce((nb, group) => {
            return (nb += log.results[group].filter(t => !!t.ignored).length);
        }, 0);
    }, [log]);
    const nbFailed = useMemo(() => {
        return Object.keys(log?.results || {}).reduce((nb, group) => {
            return (nb += log.results[group].filter(t => !t.ignored && !t.result).length);
        }, 0);
    }, [log]);

    const logResume = React.useMemo(() => (
        <div>
            <h4 className="text-center text-underline">
                { t('health.run_at', { date: log?.created_at?.format('LLL') }) }
            </h4>
            <br />
            <div className="bg-dark text-white rounded w-100 p-2">
                <h5>
                    { t('health.resume') }
                </h5>
                <ul>
                    <li>
                        <b><i className="fa fa-check" />&nbsp;&nbsp;{ t('health.nb_passed', { count: nbPassed }) }</b>
                    </li>
                    <li>
                        <b><i className="fa fa-circle-o" />&nbsp;&nbsp;{ t('health.nb_ignored', { count: nbIgnored }) }</b>
                    </li>
                    <li>
                        <b><i className="fa fa-warning" />&nbsp;&nbsp;{ t('health.nb_failed', { count: nbFailed }) }</b>
                    </li>
                </ul>
            </div>

            {Object.keys(log?.results || {}).map(key => (
                <div key={key} className="dark mt-3">
                    <div className="text-bold mb-1">
                        <i className={`fa fa-${Health.groupIcon(key)}`} />&nbsp;
                        {t(`health.groups.${key}.title`)}
                    </div>
                    {log.results[key].filter(check => !check.ignored).map((check, index) => (
                        <div className={`test bg-${check.result ? 'success' : 'danger'}`} key={index}>
                            <div className="name">
                                {t(`health.groups.${key}.${check.test}${check.result ? '' : '_failed'}`)}
                                {check.details && (
                                    <small className="details">{check.details}</small>
                                )}
                            </div>
                            <div className="result">
                                <Badge color={check.result ? 'success' : 'danger'} size="xs">
                                    <i className={`fa fa-${check.result ? 'check' : 'warning'}`} />
                                </Badge>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
            <div className="text-bold mt-3">
                <i className="fa fa-question-circle-o" />&nbsp;
                {t(`health.groups.ignored`)}
            </div>
            {Object.keys(log?.results || {}).map(key => (
                <div key={key}>
                    {log.results[key].filter(check => check.ignored).map((check, index) => (
                        <div className="test bg-black" key={index}>
                            <div className="name">
                                {t(`health.groups.${key}.${check.test}`)}
                                {check.details && (
                                    <small className="details">{check.details}</small>
                                )}
                            </div>
                            <div className="result">
                                <Badge color="dark" size="xs">
                                    <i className="fa fa-circle-o" />
                                </Badge>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    ), [ log, i18n.language ]);

    return (
        <div className={`HealthChecks${showHealthChecks ? " expanded d-block" : " reduced"}`}>
            {(showHealthChecks) &&
                <>
                    <Button color="danger" size="sm" className='float-right' onClick={hideHealthChecks}>
                        <i className='fa fa-times'></i>
                    </Button>
                    <h3 className='border-bottom mt-1'>
                        <i className='fa fa-medkit'></i>&nbsp;{t('health.panel_title')}
                    </h3>
                    <small>
                        { t('health.check_desc') }
                    </small>
                    <hr />
                    <div className="text-center">
                        <Button onClick={ check } disabled={isLoading} size="block" color="info" title={t("health.check")} className="mt-2">
                            <i className='fa fa-bolt'></i>&nbsp;
                            {t('health.check')}
                        </Button>
                    </div>
                    <hr />
                    {log && logResume}
                    {isLoading && (
                        <div className="text-center">
                            <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>
                        </div>
                    )}
                </>
            }
        </div>
    );
}

export default HealthChecks;
